<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'
import Icon, { type IconName } from './Icon.vue'
import Spinner from './Spinner.vue'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
type Size = 'xs' | 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    block?: boolean
    icon?: IconName
    iconRight?: IconName
    to?: RouteLocationRaw
    href?: string
  }>(),
  { variant: 'secondary', size: 'md', type: 'button' },
)

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-hover shadow-xs',
  secondary: 'bg-surface text-ink border border-line hover:bg-sunken hover:border-line-strong',
  ghost: 'text-muted hover:bg-sunken hover:text-ink',
  danger: 'bg-danger text-white hover:bg-danger-hover shadow-xs',
  subtle: 'bg-accent-soft text-accent hover:brightness-95',
}

const SIZES: Record<Size, string> = {
  xs: 'h-7 gap-1 px-2 text-2xs',
  sm: 'h-8 gap-1.5 px-2.5 text-xs',
  md: 'h-9 gap-1.5 px-3 text-sm',
  lg: 'h-10 gap-2 px-4 text-sm',
}

const ICON_SIZE: Record<Size, number> = { xs: 13, sm: 14, md: 16, lg: 16 }

const classes = computed(() => [
  'relative inline-flex select-none items-center justify-center rounded-md font-medium whitespace-nowrap',
  'transition-[background-color,border-color,color,box-shadow] duration-100',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  'disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45',
  VARIANTS[props.variant],
  SIZES[props.size],
  props.block ? 'w-full' : '',
])

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
  <component
    :is="to ? RouterLink : href ? 'a' : 'button'"
    v-bind="to ? { to } : href ? { href } : { type, disabled: isDisabled }"
    :aria-disabled="isDisabled || undefined"
    :aria-busy="loading || undefined"
    :class="classes"
  >
    <span
      v-if="loading"
      class="absolute inset-0 grid place-items-center"
    >
      <Spinner :size="ICON_SIZE[size]" />
    </span>
    <span :class="['contents', loading ? 'invisible' : '']">
      <slot name="icon">
        <Icon
          v-if="icon"
          :name="icon"
          :size="ICON_SIZE[size]"
        />
      </slot>
      <slot />
      <Icon
        v-if="iconRight"
        :name="iconRight"
        :size="ICON_SIZE[size]"
      />
    </span>
  </component>
</template>
